import type { GamesItem, GamesOption } from "@seagames/contracts";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { api } from "../api/client";

/**
 * The games (SEA Games, ASEAN Para Games) every competition setup page hangs
 * from. Two rows, edited by a super admin about once — cached long.
 */
export const gamesKeys = {
	all: ["games"] as const,
	options: ["games", "options"] as const,
	detail: (id: string) => ["games", "detail", id] as const,
};

/** `GET /games/options` — both games, the main games first. */
export const gamesOptionsOptions = () =>
	queryOptions({
		queryKey: gamesKeys.options,
		queryFn: ({ signal }) => api.get<GamesOption[]>("/games/options", { signal }),
		staleTime: 10 * 60_000,
	});

/** `GET /games/:id` — dates, timezone and `sports_count`. */
export const gamesDetailOptions = (id: string) =>
	queryOptions({
		queryKey: gamesKeys.detail(id),
		queryFn: ({ signal }) => api.get<GamesItem>(`/games/${id}`, { signal }),
		staleTime: 10 * 60_000,
	});

/**
 * The games a page shows when its URL names none: the main (non-para) games,
 * else the first. The API already lists the main games first; looking at
 * `is_para` rather than a hand-typed code ("SEAG") keeps working if the code
 * is ever renamed.
 */
export function pickDefaultGames(options: readonly GamesOption[]): GamesOption | undefined {
	return options.find((g) => !g.is_para) ?? options[0];
}

/**
 * `games_id` for a page whose URL may not carry one: the URL's value when set,
 * else the default games once the options have loaded. `undefined` while
 * loading (or when no games exists) — keep list queries disabled until then.
 */
export function useDefaultGamesId(fromUrl?: string): string | undefined {
	const options = useQuery({ ...gamesOptionsOptions(), enabled: fromUrl === undefined });
	if (fromUrl !== undefined) return fromUrl;
	return options.data ? pickDefaultGames(options.data)?.games_id : undefined;
}
